import { Avatar as BaseAvatar } from "@base-ui/react/avatar";
import type { ComponentPropsWithoutRef, FunctionComponent, ReactNode } from "react";
import { cn } from "./cn";

export interface AvatarProps extends Omit<ComponentPropsWithoutRef<"span">, "children"> {
  src?: string;
  alt?: string;
  /** Pixels, or antd's named sizes (small 24, default 32, large 40). */
  size?: number | "small" | "default" | "large";
  /** Shown when there is no image (or it fails to load). */
  icon?: ReactNode;
  /** Text fallback (e.g. initials) when there is no image and no icon. */
  children?: ReactNode;
}

const NAMED_SIZE = { small: 24, default: 32, large: 40 } as const;

/** Round image with an icon/initials fallback (Base UI `Avatar`). */
export const Avatar: FunctionComponent<AvatarProps> = ({
  src,
  alt,
  size = "default",
  icon,
  children,
  className,
  style,
  ...props
}) => {
  const px = typeof size === "number" ? size : NAMED_SIZE[size];

  return (
    <BaseAvatar.Root
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-noa-secondary align-middle whitespace-nowrap text-noa-on-secondary select-none [&_svg]:size-[55%]",
        className
      )}
      style={{ width: px, height: px, fontSize: px / 2, ...style }}
      {...props}
    >
      {src && <BaseAvatar.Image src={src} alt={alt} className="size-full object-cover" />}
      <BaseAvatar.Fallback className="inline-flex size-full items-center justify-center">
        {icon ?? children}
      </BaseAvatar.Fallback>
    </BaseAvatar.Root>
  );
};
